import { DrillTimesType, FpsRecordCallback, PlayerDataIntervals } from '../../types/webPlayer';
import { LPSFromGPSPath, LPSFromGPSPoint } from '../../lib/geoNew';
import { useCallback } from 'react';

/**
 * FpsCtrl is a utility class for controlling a callback function to run at a specific frame per second rate.
 * It provides methods to start, pause, and step through frames at the designated FPS rate.
 */
export class FpsCtrl {
  isPlaying = false;

  time: number | null = null;

  frame = -1;

  tref: number | undefined = undefined;

  callback: undefined | Function = undefined;

  delay = 1000;

  fps = 25;

  multiplier = 1;

  fastPlay = false;

  constructor(fps: number, callback: Function, multiplier: number = 1, fastPlay = false) {
    this.delay = 1000 / fps;
    this.fps = fps;
    this.callback = callback;
    this.multiplier = multiplier;
    this.fastPlay = fastPlay;
  }

  loop = (timestamp: number) => {
    if (this.time === null) {
      this.time = timestamp;
    }

    const seg = Math.floor((timestamp - this.time) / (this.fastPlay ? 2 : this.delay));

    if (seg > this.frame) {
      this.frame = seg;

      if (this.callback) {
        this.callback({
          frame: this.frame * this.multiplier,
        });
      }
    }

    if (this.isPlaying) {
      this.tref = requestAnimationFrame(this.loop);
    }
  };

  setMultiplier = (value: number) => {
    this.multiplier = value;
  };

  setFastPlay = (value: boolean) => {
    this.fastPlay = value;
  };

  frameRate = (newfps: number) => {
    if (!newfps) {
      return this.fps;
    }

    this.fps = newfps;
    this.delay = 1000 / this.fps;
    this.frame = -1;
    this.time = null;

    return this.fps;
  };

  start = () => {
    if (!this.isPlaying) {
      this.isPlaying = true;
      this.tref = requestAnimationFrame(this.loop);
    }
  };

  stepForward = (seconds: number) => {
    if (this.isPlaying) {
      this.pause();
    }
    this.frame += this.fps;
    this.time = (this.time || 0) + 1000 * seconds;

    if (this.callback) {
      this.callback({
        time: this.time,
        frame: this.frame,
      });
    }
  };

  stepBackward = (seconds: number) => {
    if (this.isPlaying) {
      this.pause();
    }
    this.frame -= this.fps;
    this.time = (this.time || 0) - 1000 * seconds;

    if (this.callback) {
      this.callback({
        time: this.time,
        frame: this.frame,
      });
    }
  };

  pause = () => {
    this.isPlaying = false;
    this.time = null;
    this.frame = -1;

    // if (this.isPlaying) {
    if (this.tref) {
      cancelAnimationFrame(this.tref);
    }
    // }
  };
}

export class FpsRecordCtrl {
  isPlaying = false;

  time: number = 0;

  timeIncrement: number = 66;

  timeEnd: number = 1000;

  timeStart: number = 0;

  frame = -1;

  callback: undefined | FpsRecordCallback = undefined;

  constructor(
    timeIncrement: number,
    timeStart: number,
    timeEnd: number,
    callback: FpsRecordCallback,
  ) {
    this.time = timeStart;
    this.timeStart = timeStart;
    this.timeIncrement = timeIncrement;
    this.timeEnd = timeEnd;
    this.callback = callback;
  }

  requestFrame = async (cb: Function) => {
    if (cb
      && this.isPlaying
      && (this.timeIncrement * this.frame + this.timeStart) < this.timeEnd
    ) {
      await cb();
    }
  };

  cancelAnimation() {
    this.isPlaying = false;
    this.time = 0;
  }

  loop = async () => {
    this.frame += 1;
    this.time = this.timeIncrement * this.frame + this.timeStart;

    if (this.callback) {
      await this.callback({
        time: this.time,
        frame: this.frame,
      });
    }

    await this.requestFrame(this.loop);
  };

  start = async () => {
    if (!this.isPlaying) {
      this.isPlaying = true;
      await this.requestFrame(this.loop);
    }
  };

  pause = () => {
    this.cancelAnimation();
  };
}

/**
 * Checks if the current time is near the end of any interval within a specified threshold.
 *
 * @param {number} curTime - The current time in milliseconds.
 * @param {PlayerDataIntervals} dataIntervals - An array of time intervals represented as pairs of start and end times.
 * @param {number} [threshold=20000] - The threshold in milliseconds to consider as "near the end" of an interval. Default is 20,000 milliseconds.
 * @param {Function} [cb] - Optional callback function to be invoked if the current time is near the end of an interval.
 *
 * @returns {void}
 */
export const checkNearEndOfInterval = (
  curTime: number,
  dataIntervals: PlayerDataIntervals,
  cb?: Function,
  threshold: number = 60000,
): void => {
  const isNearEnd = dataIntervals.find((interval) => {
    if (!interval[1]) return false;
    const insideInterval = interval[0] <= curTime && curTime <= interval[1];
    return insideInterval && Math.abs(interval[1] - curTime) <= threshold;
  });

  if (isNearEnd) {
    if (cb) {
      cb(isNearEnd);
    }
  }
};

export const enterFullscreen = async (elem: null | HTMLDivElement) => {
  if (elem) {
    if (elem.requestFullscreen) {
      await elem.requestFullscreen();
    // @ts-ignore
    } else if (elem.mozRequestFullScreen) { // Firefox
      // @ts-ignore
      elem.mozRequestFullScreen();
      // @ts-ignore
    } else if (elem.webkitRequestFullscreen) { // Chrome, Safari, and Opera
      // @ts-ignore
      elem.webkitRequestFullscreen();
    }
  }
};

export const exitFullscreen = async () => {
  if (document.exitFullscreen) {
    await document.exitFullscreen();
    // @ts-ignore
  } else if (document.mozCancelFullScreen) {
    // @ts-ignore
    document.mozCancelFullScreen();
    // @ts-ignore
  } else if (document.webkitExitFullscreen) {
    // @ts-ignore
    document.webkitExitFullscreen();
  }
};

export const getCurrentDrill = (currentTime: number, drillTimes: DrillTimesType) => {
  const cdIndex = drillTimes.findIndex((dt) => dt.start <= currentTime && dt.end >= currentTime);
  return cdIndex !== -1 ? cdIndex + 1 : 1;
}

export const getPathDataEntry = (data, ground, currentGround) => {
  if (!data) {
    return out;
  }

  if (ground?.groundCoordsType === 'LOCAL') {
    return newPoint = p[1] === null || p[2] === null
      ? {
        x: null,
        y: null,
        s: null,
        h: null,
        hr: null,
        t: Math.trunc(p[0] * 1000),
        x_0: null,
        y_0: null,
      }
      : {
        x: p[1],
        y: p[2],
        s: p[3],
        h: p[4],
        hr: p[5],
        t: Math.trunc(p[0] * 1000),
        x_0: null,
        y_0: null,
      };
  } else {
    try {
      const {
        vertexALatitude,
        vertexALongitude,
        vertexBLatitude,
        vertexBLongitude,
        vertexCLatitude,
        vertexCLongitude,
      } = currentGround || {};

      // trasformo i dati GPS in coordinate locali in base al ground
      return LPSFromGPSPoint(
        {
          t: Math.trunc(data[0] * 1000),
          x: data[1],
          y: data[2],
          s: data[3],
          h: data[4],
          hr: data[5],
        },
        { latitude: vertexALatitude || 0, longitude: vertexALongitude || 0 },
        { latitude: vertexBLatitude || 0, longitude: vertexBLongitude || 0 },
        { latitude: vertexCLatitude || 0, longitude: vertexCLongitude || 0 },
      );
    } catch (err) {
      console.log('Cannot compute LPS from GPS path', err, currentGround);
    }
  }
}

export const getPathDataObj = (data, ground, currentGround) => {
  let tmpPath: {
    t: number,
    x: number | null,
    x_0?: number | null,
    y: number | null,
    y_0?: number | null,
    s: number | null,
    h: number | null,
    hr?: number | null,
  }[] = [];

  // ci sono dati
  if (data && data.length > 0) {
    // è un ground LPS
    if (ground?.groundCoordsType === 'LOCAL') {
      data.forEach((p) => {
        const newPoint = p[1] === null || p[2] === null
          ? {
            x: null,
            y: null,
            s: null,
            h: null,
            hr: null,
          }
          : {
            x: p[1],
            y: p[2],
            s: p[3],
            h: p[4],
            hr: p[5],
          };

        tmpPath.push({
          t: Math.trunc(p[0] * 1000),
          x: newPoint.x,
          y: newPoint.y,
          s: newPoint.s,
          h: newPoint.h,
          hr: newPoint.hr,
        });
      });
    } else {
      try {
        const {
          vertexALatitude,
          vertexALongitude,
          vertexBLatitude,
          vertexBLongitude,
          vertexCLatitude,
          vertexCLongitude,
        } = currentGround || {};

        // trasformo i dati GPS in coordinate locali in base al ground
        tmpPath = LPSFromGPSPath(
          data.map((p) => ({
            t: Math.trunc(p[0] * 1000),
            x: p[1],
            y: p[2],
            s: p[3],
            h: p[4],
            hr: p[5],
          })),
          { latitude: vertexALatitude || 0, longitude: vertexALongitude || 0 },
          { latitude: vertexBLatitude || 0, longitude: vertexBLongitude || 0 },
          { latitude: vertexCLatitude || 0, longitude: vertexCLongitude || 0 },
        );
      } catch (err) {
        console.log('Cannot compute LPS from GPS path', err, currentGround);
      }
    }
  } else {
    console.log('No path data', data);
  }

  // trasformo in oggetto per accesso O(1)
  const pathDataObj = tmpPath.reduce((acc, curr) => {
    acc[curr.t] = {
      x: curr.x || null,
      x_0: curr.x_0 || null,
      y: curr.y || null,
      y_0: curr.y_0 || null,
      s: curr.s || null,
      h: curr.h || null,
      hr: curr.hr || null,
    };
    return acc;
  }, {});

  return pathDataObj;
}

export const findNearTimestamp = (ts: number, tss: number[]) => tss.find((t) => (t - ts) < 500 && (t - ts) >= 0);

export const findNearTimestampIndex = (ts: number, tss: number[]) => tss.findIndex((t) => Math.abs(t - ts) < 1500);


export const logger = (debug: boolean, ...content: any) => debug ? console.log(content) : null;

export const filterNeighbors = (arr: string[], X: string) => {
  const nums = arr.map((x) => Number(x));
  const xNum = Number(X);

  const sorted = [...nums].sort((a, b) => (a < b ? -1 : 1));

  const idx = sorted.findIndex(n => n === xNum);

  if (idx === -1) return arr.slice(); // X non trovato

  const exclude = new Set();

  if (idx - 1 >= 0) exclude.add(sorted[idx - 1]); // precedente

  for (let i = 1; i <= 3; i++) {
    if (idx + i < sorted.length) exclude.add(sorted[idx + i]); // successivi
  }

  exclude.add(xNum); // escludi X stesso

  // filtra solo i valori NON da escludere
  return arr.filter(x => !exclude.has(Number(x)));
}
